import { SuggestionType } from "./suggestionTypes.js";
import { addDays } from "./timeUtils.js";

const toDate = (value) => {
  const dt = value ? new Date(value) : null;
  return Number.isFinite(dt?.getTime?.()) ? dt : null;
};

const dateKey = (date) => date.toISOString().slice(0, 10);

function findNextFreeDateForStage(stageId, tasks = [], capacity = {}, fromDate, limitDays = 14) {
  const start = toDate(fromDate);
  if (!start) return null;
  const daily = capacity.dailyLoadChart || {};

  for (let i = 1; i <= limitDays; i++) {
    const candidate = addDays(start, i);
    const key = dateKey(candidate);
    const day = daily[key] || {};
    if (stageId === "drying" && (day.drying || 0) < (capacity?.dryingShelves || 9999)) {
      return candidate;
    }
    if (
      ["bisque", "glazeFiring", "glaze", "kiln"].includes(stageId) &&
      (day.firing || 0) < (capacity?.kilnVolumeLitres ? capacity.kilnVolumeLitres : 9999)
    ) {
      return candidate;
    }
  }
  return null;
}

function tasksOverDate(tasks = [], dateStr, stageMatch) {
  return tasks.filter((t) => {
    if (stageMatch && !stageMatch(t)) return false;
    const start = toDate(t.startDate || t.start);
    const end = toDate(t.dueDate || t.end || t.deadline || t.startDate);
    if (!start && !end) return false;
    const s = start || end;
    const e = end || start;
    let cursor = new Date(s);
    while (cursor <= e) {
      if (dateKey(cursor) === dateStr) return true;
      cursor = addDays(cursor, 1);
    }
    return false;
  });
}

export function buildPlannerSuggestions({ tasks = [], capacity = {}, now = new Date() }) {
  const suggestions = [];
  const dryingWarnings = capacity?.dryingWarnings || [];
  const kilnWarnings = capacity?.kilnWarnings || [];
  const timelineConflicts = capacity?.timelineConflicts || [];

  // Drying overload: propose moving drying tasks forward a day
  dryingWarnings.forEach((w, idx) => {
    const impacted = tasksOverDate(tasks, w.date, (t) => (t.stage || t.status) === "drying");
    const candidates = impacted.slice(0, 3);
    const nextFree = findNextFreeDateForStage("drying", tasks, capacity, w.date);
    candidates.forEach((task, cIdx) => {
      if (!nextFree) return;
      suggestions.push({
        id: `suggest-drying-${w.date}-${idx}-${cIdx}`,
        type: SuggestionType.MOVE_TASK_DATE,
        title: "Reduce drying overload",
        message: `Move "${task.title || "Task"}" from ${w.date} to ${nextFree.toLocaleDateString()}.`,
        taskId: task.id,
        fromDate: w.date,
        toDate: nextFree.toISOString(),
        createdAt: now.toISOString(),
        applied: false,
      });
    });
  });

  // Kiln overload: propose shifting firings
  kilnWarnings.forEach((w, idx) => {
    const impacted = tasksOverDate(
      tasks,
      w.date,
      (t) => ["bisque", "glazeFiring", "glaze", "kiln"].includes(t.stage || t.status || "")
    );
    const candidates = impacted.slice(0, 3);
    const nextFree = findNextFreeDateForStage("kiln", tasks, capacity, w.date);
    candidates.forEach((task, cIdx) => {
      if (!nextFree) return;
      suggestions.push({
        id: `suggest-kiln-${w.date}-${idx}-${cIdx}`,
        type: SuggestionType.RESCHEDULE_FIRING,
        title: "Stagger kiln load",
        message: `Move "${task.title || "Task"}" firing from ${w.date} to ${nextFree.toLocaleDateString()}.`,
        taskId: task.id,
        fromDate: w.date,
        toDate: nextFree.toISOString(),
        createdAt: now.toISOString(),
        applied: false,
      });
    });
  });

  // Timeline conflicts: generic move suggestions
  timelineConflicts
    .filter((c) => c.level === "error")
    .slice(0, 3)
    .forEach((c, idx) => {
      const impacted = tasksOverDate(tasks, c.date);
      impacted.slice(0, 2).forEach((task, tIdx) => {
        const nextFree = findNextFreeDateForStage(task.stage || task.status || "", tasks, capacity, c.date);
        if (!nextFree) return;
        suggestions.push({
          id: `suggest-conflict-${c.date}-${idx}-${tIdx}`,
          type: SuggestionType.MOVE_STAGE_TO_NEXT_SLOT,
          title: "Reduce conflict",
          message: `Move "${task.title || "Task"}" away from ${c.date} to ${nextFree.toLocaleDateString()}.`,
          taskId: task.id,
          fromDate: c.date,
          toDate: nextFree.toISOString(),
          createdAt: now.toISOString(),
          applied: false,
        });
      });
    });

  // Overdue tasks: propose nearest next free day
  tasks
    .filter((t) => {
      const d = toDate(t.dueDate || t.end || t.deadline);
      return d && d.getTime() < now.getTime() && !t.completed;
    })
    .slice(0, 3)
    .forEach((task, idx) => {
      const target = findNextFreeDateForStage(task.stage || task.status || "drying", tasks, capacity, now);
      if (!target) return;
      suggestions.push({
        id: `suggest-overdue-${task.id}-${idx}`,
        type: SuggestionType.MOVE_TASK_DATE,
        title: "Reschedule overdue task",
        message: `Move "${task.title || "Task"}" to ${target.toLocaleDateString()} to get it back on track.`,
        taskId: task.id,
        fromDate: (task.dueDate || task.end || task.deadline || now).toString(),
        toDate: target.toISOString(),
        createdAt: now.toISOString(),
        applied: false,
      });
    });

  return suggestions;
}
